import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle2, Activity, AlertTriangle } from "lucide-react";
import { ShadowModeBanner } from "@/components/admin/ShadowModeBanner";

interface ProviderRow {
  id: string;
  provider_name: string;
  models: string[];
  active: boolean;
  priority: number;
}

interface DecisionStat {
  model_used: string;
  count: number;
  is_gemini: boolean;
}

export default function ProvidersHealth() {
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [decisions, setDecisions] = useState<DecisionStat[]>([]);
  const [geminiPrimaryCount, setGeminiPrimaryCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data: provs } = await supabase
        .from("syra_ai_providers")
        .select("id, provider_name, models, active, priority")
        .order("priority");
      setProviders((provs as ProviderRow[]) ?? []);

      // Últimas 24h de model_decisions agregadas
      const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
      const { data: dec } = await supabase
        .from("model_decisions")
        .select("model_used")
        .gte("created_at", since)
        .limit(5000);

      const counts = new Map<string, number>();
      let geminiPrimary = 0;
      (dec ?? []).forEach((r: any) => {
        const m = r.model_used || "unknown";
        counts.set(m, (counts.get(m) ?? 0) + 1);
        if (m.startsWith("google/gemini")) geminiPrimary++;
      });
      const stats: DecisionStat[] = Array.from(counts.entries())
        .map(([model_used, count]) => ({
          model_used,
          count,
          is_gemini: model_used.startsWith("google/gemini"),
        }))
        .sort((a, b) => b.count - a.count);
      setDecisions(stats);
      setGeminiPrimaryCount(geminiPrimary);
      setLoading(false);
    })();
  }, []);

  const totalCalls = decisions.reduce((s, d) => s + d.count, 0);
  const geminiPct = totalCalls ? Math.round((geminiPrimaryCount / totalCalls) * 100) : 0;
  const lockdownOk = geminiPct < 5;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <ShadowModeBanner compact />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Providers Health</h1>
          <p className="text-muted-foreground">A9-01 · Anti-Gemini Lockdown · últimas 24h</p>
        </div>
        <Badge variant={lockdownOk ? "default" : "destructive"} className="text-base px-4 py-2">
          {lockdownOk ? (
            <><CheckCircle2 className="h-4 w-4 mr-1" /> Lockdown OK · {geminiPct}% Gemini</>
          ) : (
            <><AlertCircle className="h-4 w-4 mr-1" /> Lockdown FAIL · {geminiPct}% Gemini primário</>
          )}
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {providers.map((p) => (
          <Card key={p.id}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between text-lg">
                {p.provider_name}
                <Badge variant={p.active ? "default" : "secondary"}>
                  {p.active ? "Active" : "Off"}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="text-xs text-muted-foreground">Priority: {p.priority}</div>
              <div className="text-xs">
                <strong>Models ({p.models?.length ?? 0}):</strong>
                <ul className="list-disc pl-4 mt-1 max-h-32 overflow-y-auto">
                  {(p.models ?? []).map((m) => <li key={m} className="truncate">{m}</li>)}
                </ul>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Distribuição de chamadas (24h) — total: {totalCalls}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground">Carregando…</p>
          ) : decisions.length === 0 ? (
            <p className="text-muted-foreground">Nenhuma decisão registrada nas últimas 24h.</p>
          ) : (
            <div className="space-y-2">
              {decisions.map((d) => {
                const pct = totalCalls ? (d.count / totalCalls) * 100 : 0;
                return (
                  <div key={d.model_used} className="flex items-center gap-3">
                    <div className="w-64 text-sm font-mono truncate">{d.model_used}</div>
                    <div className="flex-1 bg-muted h-6 rounded relative overflow-hidden">
                      <div
                        className={`h-full ${d.is_gemini ? "bg-destructive" : "bg-primary"}`}
                        style={{ width: `${pct}%` }}
                      />
                      <div className="absolute inset-0 flex items-center px-2 text-xs">
                        {d.count} ({pct.toFixed(1)}%)
                      </div>
                    </div>
                    {d.is_gemini && (
                      <AlertTriangle className="h-4 w-4 text-destructive" aria-label="Gemini primário" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Gates A9-01</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <Gate ok={geminiPct < 5} label={`Gemini primário <5% (atual: ${geminiPct}%)`} />
          <Gate ok={providers.find(p => p.provider_name === "OpenAI")?.models?.some(m => m.includes("gpt-5")) ?? false}
                label="OpenAI BYOK contém GPT-5 family" />
          <Gate ok={providers.find(p => p.provider_name === "Anthropic")?.active ?? false}
                label="Anthropic provider ativo" />
          <Gate ok={totalCalls > 0} label={`model_decisions populando (${totalCalls}/24h)`} />
        </CardContent>
      </Card>
    </div>
  );
}

function Gate({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      {ok ? (
        <CheckCircle2 className="h-4 w-4 text-primary" />
      ) : (
        <AlertCircle className="h-4 w-4 text-destructive" />
      )}
      <span className={ok ? "" : "text-destructive"}>{label}</span>
    </div>
  );
}
